'use client'

import { DecodedPix, decodePix, payPixQrCode, sendPix } from '@/lib/api/financial/digital-account-api'
import { formatDateOnly } from '@/lib/utils/date-only'
import PaymentCodeScanner from '@/components/code-scanner/payment-code-scanner'
import { useEffect, useState } from 'react'
import { MdPhotoCamera, MdPix, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'
import PasswordField from './password-field'
import CurrencyInput from './currency-input'

const KEY_TYPES = [
    { value: 'CPF', label: 'CPF' },
    { value: 'CNPJ', label: 'CNPJ' },
    { value: 'EMAIL', label: 'E-mail' },
    { value: 'PHONE', label: 'Telefone' },
    { value: 'EVP', label: 'Chave aleatória' },
]

const QR_TYPE_LABEL: Record<DecodedPix['type'], string> = {
    STATIC: 'QR Code estático',
    DYNAMIC: 'Cobrança (QR Code dinâmico)',
    DYNAMIC_WITH_ASAAS_ADDRESS_KEY: 'Cobrança (QR Code dinâmico)',
    COMPOSITE: 'Cobrança com boleto e Pix',
}

const formatBrl = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function SendPixModal({
    initialPayload,
    onClose,
    onSuccess,
}: {
    initialPayload?: string
    onClose: () => void
    onSuccess: () => void
}) {
    const [mode, setMode] = useState<'key' | 'copy'>(initialPayload ? 'copy' : 'key')
    const [keyType, setKeyType] = useState('CPF')
    const [pixKey, setPixKey] = useState('')
    const [copyPaste, setCopyPaste] = useState(initialPayload ?? '')
    const [decoded, setDecoded] = useState<DecodedPix | null>(null)
    const [amountCents, setAmountCents] = useState<number | null>(null)
    const [description, setDescription] = useState('')
    const [password, setPassword] = useState('')
    const [decoding, setDecoding] = useState(false)
    const [sending, setSending] = useState(false)
    const [scanning, setScanning] = useState(false)

    // Valor do QR já com juros, multa e desconto; só é editável quando o QR deixa
    // pagar outro valor ou não traz valor nenhum (QR estático "aberto").
    const qrValue = Number(decoded?.totalValue || decoded?.value || 0)
    const amountEditable = mode === 'key' || !qrValue || Boolean(decoded?.canBePaidWithDifferentValue)
    const qrBlocked = mode === 'copy' && decoded?.canBePaid === false

    const decode = async (payload: string) => {
        const value = payload.trim()
        if (!value) return
        setCopyPaste(value)
        setDecoding(true)
        try {
            const res = await decodePix(value)
            if (res.success && res.result) {
                setDecoded(res.result)
                const total = Number(res.result.totalValue || res.result.value || 0)
                setAmountCents(total ? Math.round(total * 100) : null)
            } else {
                setDecoded(null)
                toast.error(res.error || 'Não foi possível ler o código PIX')
            }
        } finally {
            setDecoding(false)
        }
    }

    useEffect(() => {
        if (initialPayload) decode(initialPayload)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSend = async () => {
        if (!amountCents || amountCents <= 0) {
            toast.warning('Informe o valor')
            return
        }
        if (mode === 'key' && !pixKey.trim()) {
            toast.warning('Informe a chave PIX')
            return
        }
        if (mode === 'copy' && !decoded) {
            toast.warning('Consulte o código PIX antes de pagar')
            return
        }
        if (!password) return toast.warning('Confirme sua senha')
        setSending(true)
        try {
            // Copia e cola paga o QR (leva o identificador da cobrança); transferir
            // para a chave contida nele deixaria a cobrança de quem recebe em aberto.
            const res = mode === 'copy'
                ? await payPixQrCode({
                    payload: decoded!.payload || copyPaste.trim(),
                    valueCents: amountCents,
                    description: description.trim() || undefined,
                    password,
                })
                : await sendPix({
                    pixKey: pixKey.trim(),
                    pixKeyType: keyType,
                    amountCents,
                    description: description.trim() || undefined,
                    password,
                })
            if (res.success) {
                toast.success('PIX enviado')
                onSuccess()
            } else {
                toast.error(res.error || 'Erro ao enviar PIX')
            }
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                    <MdPix className="text-emerald-500" /> Enviar PIX
                </h3>

                <div className="tabs tabs-boxed mb-4 inline-flex">
                    <button className={`tab ${mode === 'key' ? 'tab-active' : ''}`} onClick={() => setMode('key')}>Por chave</button>
                    <button className={`tab ${mode === 'copy' ? 'tab-active' : ''}`} onClick={() => setMode('copy')}>Copia e cola</button>
                </div>

                {mode === 'key' ? (
                    <div className="space-y-3">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Tipo de chave</span>
                            <select className="select select-bordered select-sm" value={keyType} onChange={e => setKeyType(e.target.value)}>
                                {KEY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Chave PIX</span>
                            <input className="input input-bordered input-sm" value={pixKey} onChange={e => setPixKey(e.target.value)} />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Código PIX copia e cola</span>
                            <div className="flex gap-2">
                                <textarea
                                    className="textarea textarea-bordered flex-1 text-xs font-mono"
                                    rows={3}
                                    placeholder="Cole o código ou leia o QR Code com a câmera"
                                    value={copyPaste}
                                    onChange={e => {
                                        setCopyPaste(e.target.value)
                                        setDecoded(null)
                                    }}
                                />
                                <div className="flex flex-col gap-1 self-end">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost"
                                        title="Ler com a câmera"
                                        onClick={() => setScanning(true)}
                                        disabled={decoding || sending}
                                    >
                                        <MdPhotoCamera className="text-lg" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost"
                                        title="Consultar"
                                        onClick={() => decode(copyPaste)}
                                        disabled={decoding || !copyPaste.trim()}
                                    >
                                        {decoding ? <span className="loading loading-spinner loading-xs" /> : <MdSearch className="text-lg" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {decoded && (
                            <div className="rounded-xl border border-base-300 bg-base-200/50 p-3 text-sm space-y-1.5">
                                <div className="flex items-start justify-between gap-3">
                                    <span className="text-base-content/60">Recebedor</span>
                                    <span className="font-medium text-right">
                                        {decoded.receiver?.tradingName || decoded.receiver?.name || decoded.pixKey || '—'}
                                        {decoded.receiver?.cpfCnpj && (
                                            <span className="block text-xs text-base-content/50 font-normal">{decoded.receiver.cpfCnpj}</span>
                                        )}
                                        {decoded.receiver?.ispbName && (
                                            <span className="block text-xs text-base-content/50 font-normal">{decoded.receiver.ispbName}</span>
                                        )}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-base-content/60">Tipo</span>
                                    <span>{QR_TYPE_LABEL[decoded.type] ?? decoded.type}</span>
                                </div>
                                {decoded.dueDate && (
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-base-content/60">Vencimento</span>
                                        <span>{formatDateOnly(decoded.dueDate)}</span>
                                    </div>
                                )}
                                {Number(decoded.interest || 0) + Number(decoded.fine || 0) > 0 && (
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-base-content/60">Juros e multa</span>
                                        <span>{formatBrl(Number(decoded.interest || 0) + Number(decoded.fine || 0))}</span>
                                    </div>
                                )}
                                {Number(decoded.discount || 0) > 0 && (
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-base-content/60">Desconto</span>
                                        <span>- {formatBrl(Number(decoded.discount))}</span>
                                    </div>
                                )}
                                {qrValue > 0 && (
                                    <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-base-300">
                                        <span className="text-base-content/60">Valor do QR Code</span>
                                        <span className="font-semibold">{formatBrl(qrValue)}</span>
                                    </div>
                                )}
                                {decoded.description && (
                                    <p className="text-xs text-base-content/60">{decoded.description}</p>
                                )}
                                {qrBlocked && (
                                    <p className="text-xs text-error font-medium">
                                        {decoded.cannotBePaidReason || 'Este QR Code não pode ser pago.'}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Valor</span>
                        <CurrencyInput valueCents={amountCents} onChange={setAmountCents} disabled={sending || !amountEditable} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Descrição</span>
                        <input className="input input-bordered input-sm" autoComplete="off" value={description} onChange={e => setDescription(e.target.value)} />
                    </div>
                </div>

                <div className="mt-3">
                    <PasswordField value={password} onChange={setPassword} disabled={sending} />
                </div>

                <div className="modal-action">
                    <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sending}>Cancelar</button>
                    <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending || decoding || qrBlocked || (mode === 'copy' && !decoded)}>
                        {sending ? <span className="loading loading-spinner loading-xs" /> : mode === 'copy' ? 'Pagar PIX' : 'Enviar PIX'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !sending && onClose()} />

            {scanning && (
                <PaymentCodeScanner
                    accept={['pix']}
                    onClose={() => setScanning(false)}
                    onDetected={code => {
                        setScanning(false)
                        if (code.kind === 'pix') decode(code.payload)
                    }}
                />
            )}
        </div>
    )
}
